import { requestJson, responseArray, responseRecord } from './http';
import type { ActivityEntry } from '../lib/recentActivity';

export type ReportScreenshot = { type: string; data: string };

export type ReportSubmission = {
  what_happened: string;
  what_expected: string;
  product_area: string;
  route: string;
  screenshot?: ReportScreenshot;
  include_activity: boolean;
  activity?: ActivityEntry[];
};

export type MyReport = {
  receipt_ref: string;
  product_area: string;
  status: 'new' | 'triaged' | 'fixed' | 'closed';
  status_note: string;
  created_at: string;
  updated_at: string;
};

export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const SCREENSHOT_MAX_BYTES = 1_000_000;

export const PRODUCT_AREAS: readonly string[] = [
  'account', 'billing', 'projects', 'brain', 'tasks', 'devices', 'connect', 'storage',
  'managed-ai', 'receipts', 'settings', 'support', 'other',
];

/** Maps the page the customer was on to a product area; unknown pages are `other`. */
export function productAreaFromRoute(route: string): string {
  const path = (route || '').split('?')[0];
  const rules: Array<[RegExp, string]> = [
    [/^\/(billing|pricing)/, 'billing'],
    [/^\/managed-ai/, 'managed-ai'],
    [/^\/receipts/, 'receipts'],
    [/^\/devices/, 'devices'],
    [/^\/connect/, 'connect'],
    [/^\/projects\/[^/]+\/brain/, 'brain'],
    [/^\/projects\/[^/]+\/tasks/, 'tasks'],
    [/^\/projects/, 'projects'],
    [/^\/settings\/provider-keys/, 'settings'],
    [/^\/settings/, 'settings'],
    [/^\/(login|signup|password-reset|verify-email|welcome)/, 'account'],
    [/^\/support/, 'support'],
  ];
  for (const [pattern, area] of rules) if (pattern.test(path)) return area;
  return 'other';
}

/** Only a clean app path is sent: no query string, and no characters outside the server's allowlist. */
export function safeRoute(route: string): string {
  const path = (route || '').split('?')[0];
  return /^\/[A-Za-z0-9_\-/]{0,80}$/.test(path) ? path : '';
}

export async function submitProblemReport(body: ReportSubmission): Promise<{ receipt_ref: string }> {
  const payload = await requestJson('/v1/support/reports', { method: 'POST', body: JSON.stringify(body) });
  const report = responseRecord(payload, 'report');
  return { receipt_ref: String(report.receipt_ref || '') };
}

export async function listMyReports(): Promise<MyReport[]> {
  return responseArray(await requestJson('/v1/support/reports'), 'reports') as MyReport[];
}
